import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { missingPlayers } from "../../mocks/missingPlayers";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const missingPlayersJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "스포츠중계 결장자 정보",
  description: "경기 직전 확인해야 할 부상 및 결장자 현황입니다.",
  numberOfItems: missingPlayers.length,
  itemListElement: missingPlayers.map((player, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Person",
      name: player.name,
      affiliation: {
        "@type": "SportsTeam",
        name: player.team,
      },
    },
  })),
};

const importanceConfig = {
  high: { label: "핵심", color: "text-red-500 bg-red-500/10" },
  medium: { label: "주요", color: "text-brand-orange bg-brand-orange/10" },
  low: { label: "보통", color: "text-dark-text-muted bg-dark-border" },
};

export default function MissingPlayers() {
  const [filter, setFilter] = useState<"all" | "high" | "medium" | "low">("all");

  const filtered = useMemo(() => {
    if (filter === "all") return missingPlayers;
    return missingPlayers.filter((p) => p.importance === filter);
  }, [filter]);

  const filters = [
    { id: "all", label: "전체" },
    { id: "high", label: "핵심 선수" },
    { id: "medium", label: "주요 선수" },
    { id: "low", label: "보통" },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="스포츠중계 결장자 정보 | 부상 및 징계 현황 - 할매티비"
        description="스포츠중계 결장자 정보를 경기 직전에 확인하세요. 부상, 징계, 개인 사유 등 모든 결장 사유와 예상 복귀일을 실시간으로 업데이트하여 정확한 경기 분석을 돕습니다."
        keywords="스포츠중계결장자,부상선수,징계선수,결장자정보,선수부상,복귀예정일"
        canonicalPath="/missing-players"
        jsonLd={missingPlayersJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              스포츠중계 결장자 정보
            </h1>
            <p className="text-dark-text-muted text-base">
              경기 직전 확인해야 할 부상 및 결장자 현황입니다.
            </p>
          </div>

          {/* Importance Filter */}
          <div className="flex items-center gap-2 mb-8">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id as typeof filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  filter === f.id
                    ? "bg-brand-orange text-white"
                    : "bg-dark-surface text-dark-text-muted border border-dark-border hover:border-brand-orange hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Players Table */}
          <div className="space-y-3 mb-12">
            {filtered.map((player) => {
              const config = importanceConfig[player.importance];
              return (
                <div
                  key={player.id}
                  className="bg-dark-surface border border-dark-border rounded-xl p-4 md:p-5 hover:border-brand-orange transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${config.color}`}>
                        {config.label}
                      </span>
                      <div>
                        <span className="text-white font-bold text-sm md:text-base block">{player.name}</span>
                        <span className="text-dark-text-muted text-xs">{player.team} · {player.league}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 md:gap-6">
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block">포지션</span>
                        <span className="text-white font-bold text-sm">{player.position}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block">결장 사유</span>
                        <span className="text-brand-orange font-bold text-sm">{player.reason}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block">예상 복귀</span>
                        <span className="text-white font-bold text-sm">{player.expectedReturn}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 mb-12">
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <span className="text-red-500 font-bold text-2xl block">
                {missingPlayers.filter((p) => p.importance === "high").length}
              </span>
              <span className="text-dark-text-muted text-xs">핵심 결장자</span>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <span className="text-brand-orange font-bold text-2xl block">
                {missingPlayers.filter((p) => p.importance === "medium").length}
              </span>
              <span className="text-dark-text-muted text-xs">주요 결장자</span>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-xl p-4 text-center">
              <span className="text-dark-text-muted font-bold text-2xl block">
                {missingPlayers.filter((p) => p.importance === "low").length}
              </span>
              <span className="text-dark-text-muted text-xs">보통 결장자</span>
            </div>
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">스포츠중계 결장자 정보 활용법</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">스포츠중계 결장자 정보</strong>는 경기 분석의 핵심 요소입니다. 핵심 선수의 결장은 팀 전체의 전력에 큰 영향을 미치며, 배당률과 경기 흐름에 직접적인 변화를 가져옵니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">할매티비</strong>는 경기 직전까지 실시간으로 <strong className="text-white">스포츠중계 결장자 정보</strong>를 업데이트합니다. 부상, 징계, 개인 사유 등 모든 결장 사유를 명확히 안내하여 정확한 경기 예측을 돕습니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">스포츠중계</strong> 시청 전 반드시 결장자 목록을 확인하세요. 예상치 못한 선수의 결장은 경기 결과를 완전히 바꿀 수 있으며, 결장자 정보와 라인업을 함께 확인하면 완벽한 경기 분석이 가능합니다.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12 pt-8 border-t border-dark-border">
            <Link
              to="/sports-schedule"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              <span>전체 경기 일정 보기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}